import React from "react";
import { Table, Guest } from "../../api";
import JSZip from "jszip";
import { imageToBase64, createSvgFromElement } from "./ImageToBase64";
import roundTable from "../../icons/round_table.svg";
import longTable from "../../icons/long_table.svg";
import stage from "../../icons/stage.svg";
import circle from "../../icons/circle.svg";
import curvedTable from "../../icons/curved_table.svg";
import danceFloor from "../../icons/dance_floor.svg";

// 添加表格样式映射
export const tableStyleMap = {
  1: roundTable,
  2: longTable,
  3: stage,
  4: circle,
  5: curvedTable,
  6: danceFloor,
};

export const defaultTableImage = roundTable;
// 定义ChartStyle接口
interface ChartStyle {
  id: string | number;
  name: string;
  titleFont: string;
  titleSize: string;
  coupleNamesFont: string;
  coupleNamesSize: string;
  dateFont: string;
  dateSize: string;
  tableFont: string;
  tableFontSize: string;
  guestListFont: string;
  guestListFontSize: string;
  layout?: {
    header: {
      width: number;
      height: number;
    };
    table: {
      width: number;
      height: number;
    };
  };
}

interface DownloadHandlerProps {
  weddingTableId: string;
  title: string;
  coupleNames: string;
  weddingDate: string;
  tables: Table[];
  guests: Guest[];
  tableLayouts: Array<{
    id: string;
    name: string;
    x: number;
    y: number;
    width: number;
    height: number;
    imageUrl: string;
    rotation?: number;
    flipX?: boolean;
    flipY?: boolean;
  }>;
  visibleTableOrder: string[];
  selectedStyle: ChartStyle;
  showTables: boolean;
  layoutWidth: number;
  layoutHeight: number;
  scale: number;
  images: Array<{
    id: string;
    url: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
}

const GRID_SIZE = 20; // 网格在屏幕上的像素大小
const REAL_GRID_SIZE = 50; // 每个网格代表的实际厘米数

const DownloadHandler: React.FC<DownloadHandlerProps> = (props) => {
  // 将异步逻辑移到事件处理函数中

  const handleDownload = async () => {
    try {
      console.log("Starting download process...");
      if (props.selectedStyle.id === 1) {
        const element = document.querySelector(".chart-container");
        if (!element) {
          throw new Error("Chart container element not found");
        }

        // 加载字体数据
        const response = await fetch('/fonts/fonts_base64.json');
        const text = await response.text(); // 先获取原始文本
        console.log("Raw JSON response:", text); // 打印原始响应

        let base64Fonts;
        try {
          base64Fonts = JSON.parse(text);
        } catch (parseError) {
          console.error("JSON parse error:", parseError);
          // 尝试移除可能的BOM标记
          const cleanText = text.replace(/^\uFEFF/, '');
          base64Fonts = JSON.parse(cleanText);
        }

        if (!base64Fonts) {
          throw new Error('Failed to load font data');
        }

        console.log("Font data loaded successfully:", Object.keys(base64Fonts));

        const width = element.clientWidth;
        const height = element.clientHeight;

        // 获取所需字体的base64数据
        const titleFontData = base64Fonts[props.selectedStyle.titleFont];
        const coupleNamesFontData = base64Fonts[props.selectedStyle.coupleNamesFont];
        const dateFontData = base64Fonts[props.selectedStyle.dateFont];
        const tableFontData = base64Fonts[props.selectedStyle.tableFont];
        const guestListFontData = base64Fonts[props.selectedStyle.guestListFont];

        if (!titleFontData || !coupleNamesFontData || !dateFontData || !tableFontData || !guestListFontData) {
          throw new Error('部分字体数据缺失');
        }

        // 创建SVG
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("width", width.toString());
        svg.setAttribute("height", height.toString());

        const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
        const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
        style.textContent = `
          @font-face {
            font-family: '${props.selectedStyle.titleFont}';
            src: url(${titleFontData}) format('truetype');
          }
          @font-face {
            font-family: '${props.selectedStyle.coupleNamesFont}';
            src: url(${coupleNamesFontData}) format('truetype');
          }
          @font-face {
            font-family: '${props.selectedStyle.dateFont}';
            src: url(${dateFontData}) format('truetype');
          }
          @font-face {
            font-family: '${props.selectedStyle.tableFont}';
            src: url(${tableFontData}) format('truetype');
          }
          @font-face {
            font-family: '${props.selectedStyle.guestListFont}';
            src: url(${guestListFontData}) format('truetype');
          }
          .chart-title {
            font-family: '${props.selectedStyle.titleFont}';
            font-size: ${props.selectedStyle.titleSize};
            text-align: center;
            margin-bottom: 20px;
          }
          .couple-names {
            font-family: '${props.selectedStyle.coupleNamesFont}';
            font-size: ${props.selectedStyle.coupleNamesSize};
            text-align: center;
            margin-bottom: 10px;
          }
          .wedding-date {
            font-family: '${props.selectedStyle.dateFont}';
            font-size: ${props.selectedStyle.dateSize};
            text-align: center;
            margin-bottom: 30px;
          }
          .guest-list-table-name {
            font-family: '${props.selectedStyle.tableFont}';
            font-size: ${props.selectedStyle.tableFontSize};
          }
          .guest-item {
            font-family: '${props.selectedStyle.guestListFont}';
            font-size: ${props.selectedStyle.guestListFontSize};
          }
        `;

        // 在创建SVG后，添加标题组
        const titleGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");

        // 添加标题
        const titleText = document.createElementNS("http://www.w3.org/2000/svg", "text");
        titleText.setAttribute("x", (width / 2).toString());
        titleText.setAttribute("y", "80");
        titleText.setAttribute("text-anchor", "middle");
        titleText.setAttribute("font-family", props.selectedStyle.titleFont);
        titleText.setAttribute("font-size", props.selectedStyle.titleSize);
        titleText.textContent = props.title || "Wedding Seating Chart";
        titleGroup.appendChild(titleText);

        // 添加新人姓名
        const namesText = document.createElementNS("http://www.w3.org/2000/svg", "text");
        namesText.setAttribute("x", (width / 2).toString());
        namesText.setAttribute("y", "150");
        namesText.setAttribute("text-anchor", "middle");
        namesText.setAttribute("font-family", props.selectedStyle.coupleNamesFont);
        namesText.setAttribute("font-size", props.selectedStyle.coupleNamesSize);
        namesText.textContent = props.coupleNames || "";
        titleGroup.appendChild(namesText);

        // 添加婚礼日期
        const dateText = document.createElementNS("http://www.w3.org/2000/svg", "text");
        dateText.setAttribute("x", (width / 2).toString());
        dateText.setAttribute("y", "210");
        dateText.setAttribute("text-anchor", "middle");
        dateText.setAttribute("font-family", props.selectedStyle.dateFont);
        dateText.setAttribute("font-size", props.selectedStyle.dateSize);
        dateText.textContent = props.weddingDate || "";
        titleGroup.appendChild(dateText);

        // 将标题组添加到SVG中
        svg.appendChild(titleGroup);
        svg.appendChild(style);
        svg.appendChild(defs);

        // 创建图片组
        const imagesGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
        imagesGroup.setAttribute("class", "images");

        // 添加所有上传的图片
        for (const image of props.images) {
          try {
            // 创建图片元素
            const svgImage = document.createElementNS("http://www.w3.org/2000/svg", "image");
            
            // 设置图片位置和尺寸
            svgImage.setAttribute("x", (image.x * props.scale).toString());
            svgImage.setAttribute("y", (image.y * props.scale).toString());
            svgImage.setAttribute("width", (image.width * props.scale).toString());
            svgImage.setAttribute("height", (image.height * props.scale).toString());
            
            // 将图片URL转换为base64
            const imageDataUrl = image.url;
            svgImage.setAttribute("href", imageDataUrl);
            
            // 添加到图片组
            imagesGroup.appendChild(svgImage);
          } catch (e) {
            console.warn("Error processing uploaded image:", e);
          }
        }

        // 将图片组添加到SVG
        svg.appendChild(imagesGroup);

        // 只有在显示桌位图时才添加桌子
        if (props.showTables) {
          // 添加桌子布局
          const tablesGroup = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "g",
          );
          tablesGroup.setAttribute(
            "transform",
            `translate(${width / 2 - (props.layoutWidth * props.scale) / 2}, 300)`
          );

          // 修改桌子图片处理部分
          for (const table of props.tables) {
            try {
              const tableStyle = table.table_style
                ? JSON.parse(table.table_style)
                : null;
              const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
              const x = (tableStyle?.x || 0) * pixelsPerCm * props.scale;
              const y = (tableStyle?.y || 0) * pixelsPerCm * props.scale;
              const width = (tableStyle?.width || 80) * pixelsPerCm * props.scale;
              const height = (tableStyle?.height || 80) * pixelsPerCm * props.scale;

              // 添加桌子图片
              const tableImage = document.createElementNS(
                "http://www.w3.org/2000/svg",
                "image"
              );
              const imageUrl = tableStyle?.imageUrl || defaultTableImage;
              let base64Image;
              try {
                // 如果 imageUrl 是数字，从 tableStyleMap 获取
                const mappedImage = typeof imageUrl === 'number' ? tableStyleMap[imageUrl as keyof typeof tableStyleMap] : imageUrl;
                base64Image = await imageToBase64(mappedImage || defaultTableImage);
                
                // 其他检查逻辑保持不变
                if (base64Image === 'data:,' || !base64Image) {
                  base64Image = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIi8+';
                }
                
                // 确保base64Image包含正确的数据格式前缀
                const formattedBase64 = base64Image.startsWith('data:') 
                  ? base64Image 
                  : `data:image/svg+xml;base64,${base64Image}`;

                tableImage.setAttributeNS(
                  "http://www.w3.org/1999/xlink",
                  "href",
                  formattedBase64
                );
              } catch (error) {
                console.error("Error converting image to base64:", error);
                // 如果转换失败，使用stage
                tableImage.setAttributeNS(
                  "http://www.w3.org/1999/xlink",
                  "href",
                  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIi8+'
                );
              }

              tableImage.setAttribute("x", x.toString());
              tableImage.setAttribute("y", y.toString());
              tableImage.setAttribute("width", width.toString());
              tableImage.setAttribute("height", height.toString());
              
              // 从 tableStyle 中获取旋转和翻转信息
              const rotation = tableStyle?.rotation || 0;
              const flipX = tableStyle?.flipX ? -1 : 1;
              const flipY = tableStyle?.flipY ? -1 : 1;
              
              // 设置图片元素的变换
              // 注意：使用 transform-origin 设置为中心点，确保旋转和翻转正确
              const transformOriginX = width / 2;
              const transformOriginY = height / 2;
              tableImage.setAttribute("transform", 
                `translate(${x + transformOriginX}, ${y + transformOriginY}) rotate(${rotation}) scale(${flipX}, ${flipY}) translate(${-transformOriginX}, ${-transformOriginY})`
              );
              
              // 修改定位方式，由于应用了变换，不再直接设置 x 和 y
              tableImage.setAttribute("x", "0");
              tableImage.setAttribute("y", "0");

              // 如果是stage图片，添加边框
              if (imageUrl.includes("stage")) {
                // 创建一个矩形作为边框
                const border = document.createElementNS(
                  "http://www.w3.org/2000/svg",
                  "rect",
                );
                border.setAttribute("x", x.toString());
                border.setAttribute("y", y.toString());
                border.setAttribute("width", width.toString());
                border.setAttribute("height", height.toString());
                border.setAttribute("fill", "none");
                border.setAttribute("stroke", "black");
                border.setAttribute("stroke-width", "2");
                tablesGroup.appendChild(border);
              }

              tablesGroup.appendChild(tableImage);

              // 添加桌子名称
              const tableName = document.createElementNS(
                "http://www.w3.org/2000/svg",
                "text"
              );
              
              // 桌子名称应该在图片翻转时保持正确方向
              // 创建一个包含桌子名称的组
              const textGroup = document.createElementNS(
                "http://www.w3.org/2000/svg",
                "g"
              );
              
              // 设置文本位置和属性
              tableName.setAttribute("x", "0");
              tableName.setAttribute("y", "0");
              tableName.setAttribute("text-anchor", "middle");
              tableName.setAttribute("dominant-baseline", "middle");
              tableName.setAttribute("font-size", "14px");
              tableName.textContent = table.table_name;
              
              if (tableStyle?.imageUrl?.includes("stage")) {
                tableName.setAttribute("fill", "black");
              }
              
              // 先将文本添加到组中
              textGroup.appendChild(tableName);
              
              // 设置组的变换，使文本位于桌子中心并应用反向翻转以保持可读性
              textGroup.setAttribute("transform", 
                `translate(${x + width/2}, ${y + height/2}) scale(${tableStyle?.flipX ? 1 : 1}, ${tableStyle?.flipY ? 1 : 1})`
              );
              
              // 将文本组添加到桌子组
              tablesGroup.appendChild(textGroup);
            } catch (e) {
              console.warn("Error processing table:", e);
            }
          }

          svg.appendChild(tablesGroup);
        }

        // 添加宾客列表
        const addGuestList = (
          x: number,
          tables: Array<{ tableName: string; guests: string[] }>,
          align: "start" | "end",
        ) => {
          const guestGroup = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "g",
          );
          let currentY = 300; // 宾客列表的初始y坐标

          tables.forEach((table) => {
            // 添加桌子名称（加粗）
            const tableName = document.createElementNS(
              "http://www.w3.org/2000/svg",
              "text"
            );
            tableName.setAttribute("x", x.toString());
            tableName.setAttribute("y", currentY.toString());
            tableName.setAttribute("text-anchor", "middle");
            tableName.setAttribute("font-weight", "bold");
            tableName.setAttribute("font-size", "16px");
            tableName.setAttribute("font-family", props.selectedStyle.tableFont);
            tableName.setAttribute("class", "guest-list-table-name");
            tableName.textContent = table.tableName;
            guestGroup.appendChild(tableName);

            currentY += 30;

            // 根据是否显示座位图决定显示方式
            if (props.showTables) {
              // 单列显示时保持居中对齐
              table.guests.forEach((guest) => {
                const text = document.createElementNS(
                  "http://www.w3.org/2000/svg",
                  "text"
                );
                text.setAttribute("x", x.toString());
                text.setAttribute("y", currentY.toString());
                text.setAttribute("text-anchor", "middle");
                text.setAttribute("font-size", "14px");
                text.setAttribute("class", "guest-item");
                text.setAttribute("font-family", props.selectedStyle.guestListFont);
                text.setAttribute("font-size", "14px");
                text.textContent = guest;
                guestGroup.appendChild(text);
                currentY += 30;
              });
            } else {
              // 双列显示时左对齐
              const halfLength = Math.ceil(table.guests.length / 2);
              const leftGuests = table.guests.slice(0, halfLength);
              const rightGuests = table.guests.slice(halfLength);

              let maxY = currentY;

              // 左列宾客
              leftGuests.forEach((guest) => {
                const text = document.createElementNS(
                  "http://www.w3.org/2000/svg",
                  "text"
                );
                text.setAttribute("x", (x - 60).toString());
                text.setAttribute("y", currentY.toString());
                text.setAttribute("text-anchor", "start"); // 改为左对齐
                text.setAttribute("font-size", "14px");
                text.setAttribute("class", "guest-item");
                text.setAttribute("font-family", props.selectedStyle.guestListFont);
                text.setAttribute("font-size", "14px");
                text.textContent = guest.split("(")[0];
                guestGroup.appendChild(text);
                currentY += 25;
                maxY = Math.max(maxY, currentY);
              });

              // 右列宾客
              currentY = currentY - leftGuests.length * 25;
              rightGuests.forEach((guest) => {
                const text = document.createElementNS(
                  "http://www.w3.org/2000/svg",
                  "text"
                );
                text.setAttribute("x", (x + 35).toString()); // 调整x坐标
                text.setAttribute("y", currentY.toString());
                text.setAttribute("text-anchor", "start"); // 改为左对齐
                text.setAttribute("font-size", "14px");
                text.setAttribute("class", "guest-item");
                text.setAttribute("font-family", props.selectedStyle.guestListFont);
                text.setAttribute("font-size", "14px");
                text.textContent = guest.split("(")[0];
                guestGroup.appendChild(text);
                currentY += 25;
              });

              currentY = maxY;
            }

            currentY += 20; // 添加表格之间的间距
          });

          svg.appendChild(guestGroup);
        };

        // 将桌子分为四组（左侧两列，右侧两列）
        const quarterLength = Math.ceil(props.visibleTableOrder.length / 4);
        const leftColumn1 = props.visibleTableOrder.slice(0, quarterLength);
        const leftColumn2 = props.visibleTableOrder.slice(
          quarterLength,
          Math.ceil(props.visibleTableOrder.length / 2),
        );
        const rightColumn1 = props.visibleTableOrder.slice(
          Math.ceil(props.visibleTableOrder.length / 2),
          Math.ceil(props.visibleTableOrder.length / 2) +
            Math.floor(
              (props.visibleTableOrder.length -
                Math.ceil(props.visibleTableOrder.length / 2)) /
                2,
            ),
        );
        const rightColumn2 = props.visibleTableOrder.slice(
          Math.ceil(props.visibleTableOrder.length / 2) +
            Math.floor(
              (props.visibleTableOrder.length -
                Math.ceil(props.visibleTableOrder.length / 2)) /
                2,
            ),
        );

        // 修改列表位置
        if (props.showTables) {
          addGuestList(
            width * 0.15,
            leftColumn1.map((id) => {
              const table = props.tables.find((t) => t.table_id === id);
              const tableGuests = props.guests.filter((g) => g.table_id === id);
              return {
                tableName: table?.table_name || "",
                guests:
                  tableGuests.length > 0
                    ? tableGuests.map((g) => `${g.guest_name}(${g.guest_count}p)`)
                    : ["No Guests"],
              };
            }),
            "start",
          );

          addGuestList(
            width * 0.28,
            leftColumn2.map((id) => {
              const table = props.tables.find((t) => t.table_id === id);
              const tableGuests = props.guests.filter((g) => g.table_id === id);
              return {
                tableName: table?.table_name || "",
                guests:
                  tableGuests.length > 0
                    ? tableGuests.map((g) => `${g.guest_name}(${g.guest_count}p)`)
                    : ["No Guests"],
              };
            }),
            "start",
          );

          addGuestList(
            width * 0.72,
            rightColumn1.map((id) => {
              const table = props.tables.find((t) => t.table_id === id);
              const tableGuests = props.guests.filter((g) => g.table_id === id);
              return {
                tableName: table?.table_name || "",
                guests:
                  tableGuests.length > 0
                    ? tableGuests.map((g) => `${g.guest_name}(${g.guest_count}p)`)
                    : ["No Guests"],
              };
            }),
            "start",
          );

          addGuestList(
            width * 0.85,
            rightColumn2.map((id) => {
              const table = props.tables.find((t) => t.table_id === id);
              const tableGuests = props.guests.filter((g) => g.table_id === id);
              return {
                tableName: table?.table_name || "",
                guests:
                  tableGuests.length > 0
                    ? tableGuests.map((g) => `${g.guest_name}(${g.guest_count}p)`)
                    : ["No Guests"],
              };
            }),
            "start",
          );
        } else {
          addGuestList(
            width * 0.2,
            leftColumn1.map((id) => {
              const table = props.tables.find((t) => t.table_id === id);
              const tableGuests = props.guests.filter((g) => g.table_id === id);
              return {
                tableName: table?.table_name || "",
                guests:
                  tableGuests.length > 0
                    ? tableGuests.map((g) => `${g.guest_name}(${g.guest_count}p)`)
                    : ["No Guests"],
              };
            }),
            "start",
          );

          addGuestList(
            width * 0.4,
            leftColumn2.map((id) => {
              const table = props.tables.find((t) => t.table_id === id);
              const tableGuests = props.guests.filter((g) => g.table_id === id);
              return {
                tableName: table?.table_name || "",
                guests:
                  tableGuests.length > 0
                    ? tableGuests.map((g) => `${g.guest_name}(${g.guest_count}p)`)
                    : ["No Guests"],
              };
            }),
            "start",
          );

          addGuestList(
            width * 0.6,
            rightColumn1.map((id) => {
              const table = props.tables.find((t) => t.table_id === id);
              const tableGuests = props.guests.filter((g) => g.table_id === id);
              return {
                tableName: table?.table_name || "",
                guests:
                  tableGuests.length > 0
                    ? tableGuests.map((g) => `${g.guest_name}(${g.guest_count}p)`)
                    : ["No Guests"],
              };
            }),
            "start",
          );

          addGuestList(
            width * 0.8,
            rightColumn2.map((id) => {
              const table = props.tables.find((t) => t.table_id === id);
              const tableGuests = props.guests.filter((g) => g.table_id === id);
              return {
                tableName: table?.table_name || "",
                guests:
                  tableGuests.length > 0
                    ? tableGuests.map((g) => `${g.guest_name}(${g.guest_count}p)`)
                    : ["No Guests"],
              };
            }),
            "start",
          );
        }

        // 在保存之前检查SVG内容
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        console.log("SVG Content:", svgString);

        const blob = new Blob([svgString], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'seating-chart.svg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // 纸片样式：下载每个纸片为单独的SVG
        const zip = new JSZip();

        // 下载标题纸片
        const headerCard = document.querySelector(".header-card");
        if (headerCard) {
          const headerSvg = await createSvgFromElement(headerCard as HTMLElement, props.selectedStyle);
          zip.file("header.svg", headerSvg);
        }

        // 下载每个桌子的纸片
        const tableCards = document.querySelectorAll(".table-card");
        tableCards.forEach(async (card) => {
          const tableName =
            card.querySelector(".table-name")?.textContent || "table";
          const svg = await createSvgFromElement(card as HTMLElement, props.selectedStyle);
          zip.file(`${tableName}.svg`, svg);
        });

        // 生成并下载zip文件
        const content = await zip.generateAsync({ type: "blob" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(content);
        link.download = "seating_chart_cards.zip";
        link.click();
      }
    } catch (error: unknown) {
      console.error("Download failed:", error);
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      alert(`下载失败: ${errorMessage}`);
    }
  };

  return (
    <button 
      className="download-button" 
      onClick={handleDownload}
    >
      下载座位图
    </button>
  );
}

export default DownloadHandler; 